const BASE_URL = 'https://api.n8n.io';

// --- Types ---

export interface N8nTemplateUser {
  name: string;
  username: string;
  bio: string;
  verified: boolean;
  links: string[];
  avatar: string;
}

export interface N8nTemplateNodeIconData {
  type: string;
  fileBuffer: string;
}

export interface N8nTemplateNode {
  id: number;
  icon: string;
  name: string;
  displayName: string;
  typeVersion: number;
  iconData?: N8nTemplateNodeIconData;
  defaults?: { name?: string; color?: string };
}

export interface N8nTemplateCategory {
  id: number;
  name: string;
  displayName: string | null;
  icon: string;
  parent: { id: number; name: string; icon: string } | null;
}

export interface N8nTemplateSummary {
  id: number;
  name: string;
  description: string;
  totalViews: number;
  createdAt: string;
  user: N8nTemplateUser;
  nodes: N8nTemplateNode[];
}

export interface N8nTemplateDetail {
  id: number;
  name: string;
  description: string;
  totalViews: number;
  createdAt: string;
  user: N8nTemplateUser;
  nodes: N8nTemplateNode[];
  categories: { id: number; name: string }[];
  image: { id: number; url: string }[];
  workflow: {
    meta?: Record<string, unknown>;
    nodes: unknown[];
    connections: Record<string, unknown>;
    pinData?: Record<string, unknown>;
  };
  workflowInfo?: {
    nodeCount: number;
    nodeTypes: Record<string, number>;
  };
}

export interface N8nTemplateSearchResponse {
  workflows: N8nTemplateSummary[];
  totalWorkflows: number;
}

export interface N8nTemplateCategoriesResponse {
  categories: N8nTemplateCategory[];
}

// --- API Functions ---

export async function searchTemplates(params: {
  search?: string;
  page?: number;
  rows?: number;
  category?: string;
}): Promise<N8nTemplateSearchResponse> {
  const url = new URL(`${BASE_URL}/templates/search`);
  if (params.search) url.searchParams.set('search', params.search);
  if (params.page) url.searchParams.set('page', String(params.page));
  if (params.rows) url.searchParams.set('rows', String(params.rows));
  if (params.category) url.searchParams.set('category', params.category);

  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Template search failed: ${res.status}`);

  const data = await res.json();
  return {
    workflows: data.workflows ?? [],
    totalWorkflows: data.totalWorkflows ?? data.workflows?.length ?? 0,
  };
}

export async function getTemplateCategories(): Promise<N8nTemplateCategory[]> {
  const res = await fetch(`${BASE_URL}/templates/categories`);
  if (!res.ok) throw new Error(`Failed to fetch categories: ${res.status}`);

  const data: N8nTemplateCategoriesResponse = await res.json();
  return data.categories ?? [];
}

export async function getTemplateDetail(id: number): Promise<N8nTemplateDetail> {
  const res = await fetch(`${BASE_URL}/templates/workflows/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch template ${id}: ${res.status}`);

  const data = await res.json();
  return data.workflow;
}

export async function getImportableWorkflow(id: number): Promise<Record<string, unknown>> {
  // The detail endpoint includes the full workflow JSON nested inside
  const detail = await getTemplateDetail(id);
  // Build a flat importable workflow shape
  return {
    name: detail.name,
    nodes: detail.workflow.nodes,
    connections: detail.workflow.connections,
    pinData: detail.workflow.pinData ?? {},
    meta: detail.workflow.meta,
  };
}
